import { useEffect, useState, useMemo, useCallback } from "react";
import FullLogo from "../../assets/svg/logo_full.svg";
import Verified from "../../assets/svg/verified.svg";
import InformationProprietaire from "../../assets/svg/InformationProprietaire.svg";

import useStyles from "./styles";
import { MenuYearItem } from "./MenuYearItem";
import { HistoriqueItem } from "./HistoriqueItem";
import { BeforeRetranscription } from "./BeforeRetranscription";
import { useGetVehiculeNFT } from "../../api/retranscription";
import { Sidebar } from "../../containers/Sidebar/Sidebar";
import dayjs from "dayjs";
import { useLocation } from "react-router-dom";

// Define the TEvent type based on the new JSON structure
type TEvent = {
  eventType: string;
  date: string;
  who: string;
  externalRef?: string;
  bcTxId?: string;
  data?: {
    vin?: string;
    ownerId?: string;
    linkedFiles?: any[];
    data?: string;
    number?: string;
    brand?: string;
    model?: string;
    pictureCid?: string | null;
    identificationId?: string;
    type?: string;
    antiTheftNumber?: string | null;
    registerStep?: string;
    gearBox?: string;
    power?: string;
    fuel?: string;
    dateOfFirstRegistration?: string;
    status?: string | null;
    name?: string;
    note?: string;
    url_report?: string[];
    eventType?: string;
    date?: string;
  };
};

// Define the vehicle type to include the new nftData structure
type TVehiculeNFT = {
  id?: string;
  nftData?: {
    metadatas?: {
      vin?: string;
      ownerId?: string;
      linkedFiles?: any[];
      data?: string;
      number?: string;
      brand?: string;
      model?: string;
      pictureCid?: string | null;
      identificationId?: string;
      type?: string;
      antiTheftNumber?: string | null;
      registerStep?: string;
      gearBox?: string;
      power?: string;
      fuel?: string;
      dateOfFirstRegistration?: string;
      status?: string | null;
    };
    events?: TEvent[];
  };
  // Keep backward compatibility with old structure
  brand?: string;
  model?: string;
  number?: string;
  gearBox?: string;
  dateOfFirstRegistration?: string;
  fuel?: string;
  power?: string;
  events?: any[];
  metadata?: any;
};

// Helper function to group events by year
const groupEventsByYear = (events: TEvent[]) => {
  const grouped: { [year: number]: TEvent[] } = {};
  
  if (!events || !Array.isArray(events)) {
    return grouped;
  }
  
  events.forEach((event, index) => {
    try {
      if (!event?.date) {
        console.warn(`Event at index ${index} has no date:`, event);
        return;
      }
      
      const eventDate = new Date(event.date);
      
      if (isNaN(eventDate.getTime())) {
        console.warn(`Event at index ${index} has invalid date:`, event.date);
        return;
      }
      
      const year = eventDate.getFullYear();
      
      if (!grouped[year]) {
        grouped[year] = [];
      }
      
      grouped[year].push(event);
      
    } catch (error) {
      console.error(`Error processing event at index ${index}:`, error);
    }
  });
  
  return grouped;
};

const Retranscription = () => {
  const classes = useStyles();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const immatriculation = queryParams.get("immatriculation");

  const [immat, setImmat] = useState("");

  const { vehicle, isSuccess }: { vehicle: TVehiculeNFT | undefined; isSuccess: boolean } = useGetVehiculeNFT(
    immatriculation ? immatriculation : immat
  );

  console.log("Vehicle data:", vehicle);

  const [isSelectedYear, setIsSelectedYear] = useState<number>(2024);

  // Process events from the new structure
  const parseEvents = useCallback((eventsData: TEvent[]): TEvent[] => {
    console.log("eventsData", eventsData);
    
    if (!eventsData || !Array.isArray(eventsData)) {
      console.log('No events array provided');
      return [];
    }
    
    // Events are now directly accessible as an array
    return eventsData.map((event, index) => {
      try {
        // Create a normalized event object
        const normalizedEvent: TEvent = {
          eventType: event.eventType || 'Unknown',
          date: event.date || new Date().toISOString(),
          who: event.who || 'Unknown',
          externalRef: event.externalRef,
          bcTxId: event.bcTxId,
          data: event.data || {}
        };
        
        return normalizedEvent;
      } catch (error) {
        console.error(`Error processing event at index ${index}:`, error);
        return {
          eventType: 'Unknown',
          date: new Date().toISOString(),
          who: 'Unknown',
          data: {}
        };
      }
    });
  }, []);

  // Enhanced vehicle data parsing for new structure
  const vehicleInfo = useMemo(() => {
    if (!vehicle) return null;
    
    // First, try to get data from the new nftData.metadatas structure
    if (vehicle.nftData?.metadatas) {
      const metadatas = vehicle.nftData.metadatas;
      return {
        brand: metadatas.brand || '',
        model: metadatas.model || '',
        number: metadatas.number || '',
        gearBox: metadatas.gearBox || '',
        fuel: metadatas.fuel || '',
        power: metadatas.power || '',
        dateOfFirstRegistration: metadatas.dateOfFirstRegistration || '',
        vin: metadatas.vin || '',
        ownerId: metadatas.ownerId || '',
        type: metadatas.type || '',
        antiTheftNumber: metadatas.antiTheftNumber || '',
        registerStep: metadatas.registerStep || '',
        status: metadatas.status || '',
      };
    }
    
    // Fallback to old structure for backward compatibility
    let parsedMetadata = vehicle.metadata;
    if (typeof vehicle.metadata === 'string') {
      try {
        parsedMetadata = JSON.parse(vehicle.metadata);
      } catch (error) {
        console.error('Failed to parse metadata:', error);
        parsedMetadata = {};
      }
    }
    
    if (parsedMetadata?.metadata) {
      try {
        parsedMetadata = JSON.parse(parsedMetadata.metadata);
      } catch (error) {
        console.error('Failed to parse nested metadata:', error);
        parsedMetadata = {};
      }
    }
    
    return {
      brand: parsedMetadata?.brand || vehicle.brand || '',
      model: parsedMetadata?.model || vehicle.model || '',
      number: parsedMetadata?.number || vehicle.number || '',
      gearBox: parsedMetadata?.gearBox || vehicle.gearBox || '',
      fuel: parsedMetadata?.fuel || vehicle.fuel || '',
      power: parsedMetadata?.power || vehicle.power || '',
      dateOfFirstRegistration: parsedMetadata?.dateOfFirstRegistration || vehicle.dateOfFirstRegistration || '',
    };
  }, [vehicle]);
  
  // Process events and group by year
  const eventsByYear = useMemo(() => {
    let eventsToProcess: TEvent[] = [];
    
    // First, try to get events from the new nftData.events structure
    if (vehicle?.nftData?.events && Array.isArray(vehicle.nftData.events)) {
      eventsToProcess = vehicle.nftData.events;
      console.log('Using events from nftData.events');
    } 
    // Fallback to old structure for backward compatibility
    else if (vehicle?.events && Array.isArray(vehicle.events)) {
      eventsToProcess = parseEvents(vehicle.events);
      console.log('Using events from old structure');
    } else {
      console.log('No events found in either structure');
      return {};
    }
    
    console.log('Events to process:', eventsToProcess);
    
    const groupedEvents = groupEventsByYear(eventsToProcess);
    console.log('Grouped events by year:', groupedEvents);
    
    return groupedEvents;
  }, [vehicle?.nftData?.events, vehicle?.events, parseEvents]);

  // Set initial selected year to the latest year with events
  useEffect(() => {
    if (isSuccess && vehicleInfo?.brand && eventsByYear && Object.keys(eventsByYear).length > 0) {
      const years = Object.keys(eventsByYear).map(Number).sort((a, b) => b - a);
      const latestYear = years[0];
      if (latestYear && !isNaN(latestYear)) {
        setIsSelectedYear(latestYear);
      }
    }
  }, [isSuccess, vehicleInfo, eventsByYear]);

  const handleChangeCurrentEvent = (date: number) => {
    setIsSelectedYear(date);
  };

  const onClickSearch = (value: string) => {
    setImmat(value);
  };

  return (
    <Sidebar
      styles={{
        sidebarRightRoot: classes.vehiculeRoot,
        sidebarRoot: classes.contentMain,
      }}
    >
      {immatriculation || vehicleInfo?.brand ? (
        <div className={classes.container}>
          <div className={classes.banner}>
            <div>
              <div>
                <img src={FullLogo} alt="Logo" />
              </div>
              <div className={classes.titleVehicule}>
                Historique du véhicule
              </div>
            </div>
          </div>
          <div className={classes.content}>
            <div className={classes.contentLeft}>
              <div className={classes.title}>
                <div className={classes.titlevehicule2}>
                  {`${vehicleInfo?.brand ?? ""} ${vehicleInfo?.model ?? ""}`}
                </div>
                <div className={classes.titleNumber}>{vehicleInfo?.number}</div>
              </div>
              <div>
                <div>
                  <div
                    className={classes.menulist}
                    style={{ marginTop: "40px" }}
                  >
                    <div>Marque</div>
                    <div>{vehicleInfo?.brand}</div>
                  </div>
                  <div className={classes.menulist}>
                    <div>Modèle</div>
                    <div>{vehicleInfo?.model}</div>
                  </div>
                  <div className={classes.menulist}>
                    <div>Immatriculation</div>
                    <div>{vehicleInfo?.number}</div>
                  </div>
                  <div className={classes.menulist}>
                    <div>Boite</div>
                    <div>{vehicleInfo?.gearBox}</div>
                  </div>
                  <div className={classes.menulist}>
                    <div>Date de mise en circulation</div>
                    <div>
                      {vehicleInfo?.dateOfFirstRegistration && 
                        dayjs(vehicleInfo.dateOfFirstRegistration).format("DD/MM/YYYY")
                      }
                    </div>
                  </div>
                  <div className={classes.menulist}>
                    <div>Carburant</div>
                    <div>{vehicleInfo?.fuel}</div>
                  </div>
                  <div className={classes.menulist}>
                    <div>Puissance</div>
                    <div>{vehicleInfo?.power}</div>
                  </div>
                </div>
              </div>
            </div>
            <div className={classes.contentRight}>
              <div className={classes.containerInformation}>
                <div className={classes.information}>
                  <img src={Verified} alt="Verified" />
                  <div className={classes.textInformation}>
                    Intervention vérifiée et certifiée par CodeNekt
                  </div>
                </div>
                <div className={classes.information}>
                  <img src={InformationProprietaire} alt="Infos" />
                  <div className={classes.textInformation}>
                    Information déclarée par le propriétaire
                  </div>
                </div>
              </div>
              <div className={classes.historique}>
                <div className={classes.containerHistorique}>
                  <div>Historique</div>
                  <div className={classes.containerYears}>
                    {Object.keys(eventsByYear)
                      .map(Number)
                      .sort((a, b) => b - a)
                      .map((year, index) => {
                        if (year >= 2005) {
                          return (
                            <MenuYearItem
                              year={year}
                              key={index}
                              onClick={() => handleChangeCurrentEvent(year)}
                              isSelected={year === isSelectedYear}
                            />
                          );
                        }
                        return null;
                      })}
                  </div>
                </div>
                <div className={classes.historiqueList}>
                  {eventsByYear[isSelectedYear]?.map((event, index) => {
                    return (
                      <div key={index}>
                        <HistoriqueItem
                          isVerified={new Date().getFullYear() === isSelectedYear}
                          historique={event}
                        />
                        {/* Display blockchain information */}
                        {(event.externalRef || event.bcTxId) && (
                          <div style={{ 
                            marginTop: '10px', 
                            marginBottom: '15px', 
                            padding: '10px', 
                            backgroundColor: '#f8f9fa', 
                            borderRadius: '4px',
                            fontSize: '12px'
                          }}>
                            {event.externalRef && (
                              <div style={{ marginBottom: '5px' }}>
                                <strong>Référence externe:</strong> {event.externalRef}
                              </div>
                            )}
                            {event.bcTxId && (
                              <div>
                                <strong>Transaction blockchain:</strong>{' '}
                                <a 
                                  href={`${process.env.REACT_APP_BLOCKCHAIN_EXPLORER_URL}${event.bcTxId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ 
                                    color: '#007bff', 
                                    textDecoration: 'none',
                                    wordBreak: 'break-all'
                                  }}
                                  onMouseOver={(e) => e.currentTarget.style.textDecoration = 'underline'}
                                  onMouseOut={(e) => e.currentTarget.style.textDecoration = 'none'}
                                >
                                  {event.bcTxId}
                                </a>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <BeforeRetranscription
          onClick={(val) => onClickSearch(val)}
          isloading={false}
        />
      )}
    </Sidebar>
  );
};

export default Retranscription;